import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import FundingDealBoard from '../../components/funding/FundingDealBoard';
import FundingDealDetail from '../../components/funding/FundingDealDetail';
import FundingDealFilters from '../../components/funding/FundingDealFilters';
import FundingDealForm from '../../components/funding/FundingDealForm';
import FundingDealMetrics from '../../components/funding/FundingDealMetrics';
import FundingDealStageBadge from '../../components/funding/FundingDealStageBadge';
import FundingDealTaskList from '../../components/funding/FundingDealTaskList';
import FundingPanel from '../../components/funding/FundingPanel';
import FundingSectionPage from '../../components/funding/FundingSectionPage';
import FundingAIAdvisorActionLink from '../../components/funding/FundingAIAdvisorActionLink';
import { useFundingWorkspace } from '../../context/FundingWorkspaceContext';
import { useFundingDeals } from '../../hooks/funding/useFundingDeals';
import { useFundingCommissions } from '../../hooks/funding/useFundingCommissions';
import { useFundingRoutes } from '../../hooks/funding/useFundingRoutes';
import { emptyFundingDealForm } from '../../services/funding/fundingDealsService';
import { fetchFundingClients } from '../../services/funding/fundingClientsService';

const formatCurrency = (value) => {
  if (value == null || value === '') return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
};

const FundingPipelinePage = () => {
  const navigate = useNavigate();
  const { commissions: commissionsPath } = useFundingRoutes();
  const { workspaceAgencyId, isPlatformContext, requiresAgencySelection } = useFundingWorkspace();
  const [viewMode, setViewMode] = useState('board');
  const [search, setSearch] = useState('');
  const [stage, setStage] = useState('');
  const [lenderType, setLenderType] = useState('');
  const [productType, setProductType] = useState('');
  const [assignedUserId, setAssignedUserId] = useState('');
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [formValues, setFormValues] = useState(emptyFundingDealForm());
  const [formClientId, setFormClientId] = useState('');
  const [clients, setClients] = useState([]);
  const [formError, setFormError] = useState(null);
  const [savingId, setSavingId] = useState('');
  const [creatingCommissionId, setCreatingCommissionId] = useState('');
  const [commissionSuccessMessage, setCommissionSuccessMessage] = useState('');

  const agencyId = isPlatformContext ? workspaceAgencyId || undefined : workspaceAgencyId;

  const { createFromDeal: createCommissionFromDeal } = useFundingCommissions({ agencyId, autoLoad: false });

  const {
    deals,
    loading,
    saving,
    error,
    create,
    update,
    remove,
    load,
  } = useFundingDeals({
    agencyId,
    filters: { stage, lenderType, productType, assignedUserId, search },
    autoLoad: Boolean(agencyId) || isPlatformContext,
  });

  const filteredDeals = useMemo(() => {
    if (!search.trim()) return deals;
    const term = search.trim().toLowerCase();
    return deals.filter((deal) => (
      (deal.clientName || '').toLowerCase().includes(term)
      || (deal.businessName || '').toLowerCase().includes(term)
      || (deal.lenderName || '').toLowerCase().includes(term)
      || (deal.productType || '').toLowerCase().includes(term)
    ));
  }, [deals, search]);

  const openCreateForm = async () => {
    setFormError(null);
    setFormValues(emptyFundingDealForm());
    if (agencyId) {
      try {
        const result = await fetchFundingClients({ agencyId });
        setClients(result.clients);
        setFormClientId(result.clients[0]?.id || '');
      } catch {
        setClients([]);
      }
    }
    setFormOpen(true);
  };

  const handleCreate = async () => {
    if (!formClientId || !agencyId) {
      setFormError('Select a client and agency before creating a deal.');
      return;
    }
    setFormError(null);
    try {
      await create({
        lender_name: formValues.lender_name,
        lender_type: formValues.lender_type || undefined,
        product_type: formValues.product_type,
        requested_amount: formValues.requested_amount ? Number(formValues.requested_amount) : undefined,
        approved_amount: formValues.approved_amount ? Number(formValues.approved_amount) : undefined,
        funded_amount: formValues.funded_amount ? Number(formValues.funded_amount) : undefined,
        stage: formValues.stage,
        probability: formValues.probability ? Number(formValues.probability) : undefined,
        commission_type: formValues.commission_type,
        commission_rate: formValues.commission_rate ? Number(formValues.commission_rate) : undefined,
        close_date: formValues.close_date || undefined,
        clawback_risk: formValues.clawback_risk,
        assigned_user_id: formValues.assigned_user_id || undefined,
        next_task: formValues.next_task || undefined,
        next_task_due_at: formValues.next_task_due_at || undefined,
        notes: formValues.notes || undefined,
      }, formClientId, agencyId);
      setFormOpen(false);
    } catch (err) {
      setFormError(err?.message || 'Failed to create deal');
    }
  };

  const handleStageChange = async (deal, nextStage) => {
    if (!agencyId) return;
    setSavingId(deal.id);
    try {
      const updated = await update(deal.id, { stage: nextStage }, agencyId);
      if (selectedDeal?.id === deal.id) setSelectedDeal(updated);
    } finally {
      setSavingId('');
    }
  };

  const handleDelete = async (deal) => {
    if (!agencyId) return;
    const confirmed = window.confirm(`Withdraw deal for ${deal.lenderName}?`);
    if (!confirmed) return;
    setSavingId(deal.id);
    try {
      await remove(deal.id, agencyId);
      if (selectedDeal?.id === deal.id) setSelectedDeal(null);
    } finally {
      setSavingId('');
    }
  };

  const handleCreateCommission = async (deal) => {
    if (!agencyId) return;
    setCreatingCommissionId(deal.id);
    setCommissionSuccessMessage('');
    try {
      const commission = await createCommissionFromDeal(deal.id, agencyId);
      setCommissionSuccessMessage(`Commission ${commission?.status || 'record'} created.`);
    } catch (err) {
      window.alert(err?.message || 'Failed to create commission');
    } finally {
      setCreatingCommissionId('');
    }
  };

  return (
    <FundingSectionPage
      title="Deal Pipeline"
      subtitle="Kanban view of funded, approved, and in-flight deals with projected commissions."
      activeStage="funded"
      actions={(
        <div className="flex gap-2">
          <FundingAIAdvisorActionLink label="Review pipeline risks" prompt="Review pipeline risks" />
          <Button variant="outline" className="rounded-xl" onClick={() => setViewMode(viewMode === 'board' ? 'table' : 'board')}>
            {viewMode === 'board' ? 'Table view' : 'Board view'}
          </Button>
          <Button className="rounded-xl bg-[var(--fg-accent)] hover:bg-[#126b4f]" onClick={openCreateForm} disabled={requiresAgencySelection}>
            New Deal
          </Button>
        </div>
      )}
    >
      <FundingPanel title="Pipeline metrics" subtitle="Estimated amounts and projected commissions across your deal book">
        <FundingDealMetrics deals={filteredDeals} />
      </FundingPanel>

      <FundingPanel title="Deal pipeline" subtitle="Track stages, probability, commissions, and next tasks">
        {requiresAgencySelection ? (
          <p className="mb-4 text-sm text-[var(--fg-muted)]">Select an agency in the header to load deals.</p>
        ) : null}

        <div className="space-y-4">
          <FundingDealFilters
            search={search}
            stage={stage}
            lenderType={lenderType}
            productType={productType}
            assignedUserId={assignedUserId}
            onSearchChange={setSearch}
            onStageChange={setStage}
            onLenderTypeChange={setLenderType}
            onProductTypeChange={setProductType}
            onAssignedUserChange={setAssignedUserId}
          />

          {error ? (
            <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-900/40 dark:bg-rose-950/30 dark:text-rose-200">
              {error}
            </div>
          ) : null}

          {commissionSuccessMessage ? (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800 dark:border-emerald-900/40 dark:bg-emerald-950/30 dark:text-emerald-200">
              <div>{commissionSuccessMessage}</div>
              <button type="button" onClick={() => navigate(commissionsPath)} className="mt-2 font-medium text-[var(--fg-accent)]">
                Open Commissions
              </button>
            </div>
          ) : null}

          {formOpen ? (
            <div className="rounded-2xl border border-[var(--fg-line)] bg-[var(--fg-panel-soft)] p-4">
              <div className="mb-4 text-sm font-semibold text-[var(--fg-text)]">Create deal</div>
              <label className="mb-4 block text-sm">
                <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-[var(--fg-muted)]">Funding client</span>
                <select value={formClientId} onChange={(e) => setFormClientId(e.target.value)} className="w-full rounded-xl border border-[var(--fg-line)] bg-white px-3 py-2 text-sm dark:bg-slate-950/20">
                  <option value="">Select client</option>
                  {clients.map((client) => <option key={client.id} value={client.id}>{client.name}</option>)}
                </select>
              </label>
              <FundingDealForm values={formValues} onChange={setFormValues} onSubmit={handleCreate} saving={saving} error={formError} />
              <button type="button" onClick={() => setFormOpen(false)} className="mt-3 text-sm text-[var(--fg-muted)]">Cancel</button>
            </div>
          ) : null}

          {loading ? (
            <p className="text-sm text-[var(--fg-muted)]">Loading deals...</p>
          ) : filteredDeals.length === 0 ? (
            <div className="rounded-xl border border-dashed border-[var(--fg-line)] bg-[var(--fg-panel-soft)] px-4 py-10 text-center">
              <p className="text-sm font-medium text-[var(--fg-text)]">No deals yet</p>
              <p className="mt-1 text-sm text-[var(--fg-muted)]">Create a deal manually or from an approved/funded submission.</p>
            </div>
          ) : viewMode === 'board' ? (
            <FundingDealBoard
              deals={filteredDeals}
              onSelectDeal={setSelectedDeal}
              onStageChange={handleStageChange}
              onCreateCommission={handleCreateCommission}
              creatingCommissionId={creatingCommissionId}
              saving={Boolean(savingId)}
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-[var(--fg-line)] text-[var(--fg-muted)]">
                    <th className="px-3 py-2 font-medium">Client</th>
                    <th className="px-3 py-2 font-medium">Lender</th>
                    <th className="px-3 py-2 font-medium">Product</th>
                    <th className="px-3 py-2 font-medium">Requested</th>
                    <th className="px-3 py-2 font-medium">Approved/Funded</th>
                    <th className="px-3 py-2 font-medium">Stage</th>
                    <th className="px-3 py-2 font-medium">Expected</th>
                    <th className="px-3 py-2 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDeals.map((deal) => (
                    <tr key={deal.id} className="border-b border-[var(--fg-line)]/70">
                      <td className="px-3 py-3 font-medium text-[var(--fg-text)]">{deal.clientName}</td>
                      <td className="px-3 py-3 text-[var(--fg-muted)]">{deal.lenderName}</td>
                      <td className="px-3 py-3 text-[var(--fg-muted)]">{deal.productType}</td>
                      <td className="px-3 py-3">{formatCurrency(deal.requestedAmount)}</td>
                      <td className="px-3 py-3">{formatCurrency(deal.fundedAmount ?? deal.approvedAmount)}</td>
                      <td className="px-3 py-3"><FundingDealStageBadge stage={deal.stage} /></td>
                      <td className="px-3 py-3">{formatCurrency(deal.expectedCommission)}</td>
                      <td className="px-3 py-3">
                        <div className="flex flex-wrap gap-1">
                          <button type="button" onClick={() => setSelectedDeal(deal)} className="rounded-lg px-2 py-1 text-xs text-[var(--fg-accent)] hover:bg-[var(--fg-accent-soft)]">View</button>
                          <button type="button" onClick={() => handleDelete(deal)} className="rounded-lg px-2 py-1 text-xs text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/30">Withdraw</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </FundingPanel>

      <div className="mt-4 grid gap-4 xl:grid-cols-[1fr_1fr]">
        {selectedDeal ? (
          <FundingPanel title="Deal detail" subtitle="Stage, amounts, projected commission, and next task">
            <FundingDealDetail
              deal={selectedDeal}
              agencyId={agencyId}
              onClose={() => setSelectedDeal(null)}
              onStageChange={handleStageChange}
              onCreateCommission={handleCreateCommission}
              creatingCommission={creatingCommissionId === selectedDeal.id}
              saving={savingId === selectedDeal.id}
            />
          </FundingPanel>
        ) : null}

        <FundingPanel title="Upcoming tasks" subtitle="Next pipeline actions across active deals">
          <FundingDealTaskList deals={filteredDeals} onSelect={setSelectedDeal} />
        </FundingPanel>
      </div>
    </FundingSectionPage>
  );
};

export default FundingPipelinePage;
